import idx from "idx";
import { toPercent, toUSD } from "src/util/currency-formatter";
import { roundValue } from "src/util/decimal-utils";

export const getTotalPercentComplete = (tasks = []) => {
  let percentageComplete = 0;
  for (const iterator of tasks) {
    percentageComplete += Number(iterator.percentageComplete) || 0.0;
  }
  if (percentageComplete)
    percentageComplete = percentageComplete / (tasks.length || 1);
  return percentageComplete || 0;
};

export const findSectionCost = (data, key) => {
  if (!data) return null;
  const foundData = data.find((item) => Object.keys(item).includes(key));
  return foundData ? foundData[key] : null;
};

export const costCategories = ["material", "labor", "subcontractor"];

export const defaultCost = {
  material: { cost: 0.0, sale: 0.0, profit: 0.0 },
  labor: { cost: 0.0, sale: 0.0, profit: 0.0 },
  subcontractor: { cost: 0.0, sale: 0.0, profit: 0.0 },
  total: 0.0,
  subtotal: 0.0,
  total_profit: 0.0,
  forcast: 0.0,
  completionPercentage: 0.0,
};

function aggregateSubsectionCosts(stage, sectionCalculationData) {
  const result = {
    material: { cost: 0.0, sale: 0.0, profit: 0.0 },
    labor: { cost: 0.0, sale: 0.0, profit: 0.0 },
    subcontractor: { cost: 0.0, sale: 0.0, profit: 0.0 },
    total: 0.0,
    subtotal: 0.0,
    total_profit: 0.0,
    forcast: 0.0,
    completionPercentage: 0.0,
  };
  let count = idx(stage, (_) => _.stages.length) || 0;

  if (stage.stages && count > 0) {
    for (const subStage of stage.stages) {
      let subSectionCost;
      if (subStage.stages === null) {
        const sectionCost =
          findSectionCost(sectionCalculationData, subStage.id) || defaultCost;
        subSectionCost = {
          ...sectionCost,
          completionPercentage: getTotalPercentComplete(subStage.tasks) || 0.0,
        };
      } else
        subSectionCost = aggregateSubsectionCosts(
          subStage,
          sectionCalculationData
        );

      costCategories.forEach((category) => {
        if (
          subSectionCost[category] &&
          !isNaN(subSectionCost[category].cost) &&
          !isNaN(subSectionCost[category].sale) &&
          !isNaN(subSectionCost[category].profit)
        ) {
          result[category].cost += subSectionCost[category].cost;
          result[category].sale += subSectionCost[category].sale;
          result[category].profit += subSectionCost[category].profit;
        }
      });

      result.total += subSectionCost.total;
      result.subtotal += subSectionCost.subtotal;
      result.total_profit += subSectionCost.total_profit;
      result.forcast += subSectionCost.forcast;
      result.completionPercentage += subSectionCost.completionPercentage;
    }

    costCategories.forEach((category) => {
      if (count > 0) {
        result[category].cost /= count;
        result[category].sale /= count;
        result[category].profit /= count;
      }
    });
  }

  return result;
}

export const updateTotals = (stageArray, sectionCalculationData) => {
  return stageArray.map((stage) => {
    if (stage.stages === null) {
      const sectionCost =
        findSectionCost(sectionCalculationData, stage.id) || defaultCost;

      return {
        ...stage,
        sectionCost: {
          ...sectionCost,
          completionPercentage: getTotalPercentComplete(stage.tasks) || 0.0,
        },
      };
    } else {
      const aggregatedCosts = aggregateSubsectionCosts(
        stage,
        sectionCalculationData
      );
      let updatedStage = { ...stage, sectionCost: aggregatedCosts };

      if (stage.stages && stage.stages.length > 0) {
        updatedStage = {
          ...updatedStage,
          stages: updateTotals(stage.stages, sectionCalculationData),
        };
      }

      return updatedStage;
    }
  });
};

export const getFormatedData = (totalCost) => {
  let materialSaleAmount = 0.0,
    laborSaleAmount = 0.0,
    subcontractorSaleAmount = 0.0,
    materialCostAmount = 0.0,
    laborCostAmount = 0.0,
    subcontractorCostAmount = 0.0,
    totalCostAmount = 0.0,
    totalSaleAmount = 0.0,
    materialProfit = 0.0,
    laborProfit = 0.0,
    subcontractorProfit = 0.0,
    totalProfit = 0.0,
    materialProfitPercentage = 0.0,
    laborProfitPercentage = 0.0,
    subcontractorProfitPercentage = 0.0,
    totalProfitPercentage = 0.0,
    completionPercentage = 0.0,
    variance = 0.0,
    utilisationRate = 0.0,
    effortCompletionPercentage = 0.0,
    forecastHoursDisplay = "0 hrs";

  materialSaleAmount = toUSD(idx(totalCost, (_) => _.materialSaleAmount) || 0);
  laborSaleAmount = toUSD(idx(totalCost, (_) => _.laborSaleAmount) || 0);
  subcontractorSaleAmount = toUSD(
    idx(totalCost, (_) => _.subcontractorSaleAmount) || 0
  );
  totalSaleAmount = toUSD(idx(totalCost, (_) => _.totalSaleAmount) || 0);
  materialCostAmount = toUSD(idx(totalCost, (_) => _.materialCost) || 0);
  laborCostAmount = toUSD(idx(totalCost, (_) => _.laborCost) || 0);
  subcontractorCostAmount = toUSD(
    idx(totalCost, (_) => _.subcontractorCost) || 0
  );
  totalCostAmount = toUSD(idx(totalCost, (_) => _.totalCost) || 0);
  materialProfit = toUSD(idx(totalCost, (_) => _.materialProfit) || 0);
  laborProfit = toUSD(idx(totalCost, (_) => _.laborProfit) || 0);
  subcontractorProfit = toUSD(
    idx(totalCost, (_) => _.subcontractorProfit) || 0
  );
  totalProfit = toUSD(idx(totalCost, (_) => _.totalProfit) || 0);
  
  // Manually compute profit percentages using (profit/cost)*100
  const materialCostValue = idx(totalCost, (_) => _.materialCost) || 0;
  const laborCostValue = idx(totalCost, (_) => _.laborCost) || 0;
  const subcontractorCostValue = idx(totalCost, (_) => _.subcontractorCost) || 0;
  const totalCostValue = idx(totalCost, (_) => _.totalCost) || 0;
  
  const materialProfitValue = idx(totalCost, (_) => _.materialProfit) || 0;
  const laborProfitValue = idx(totalCost, (_) => _.laborProfit) || 0;
  const subcontractorProfitValue = idx(totalCost, (_) => _.subcontractorProfit) || 0;
  const totalProfitValue = idx(totalCost, (_) => _.totalProfit) || 0;
  
  // Manual calculation using toPercent helper: (profit/cost)*100
  materialProfitPercentage = toPercent(materialProfitValue, materialCostValue);
  laborProfitPercentage = toPercent(laborProfitValue, laborCostValue);
  subcontractorProfitPercentage = toPercent(subcontractorProfitValue, subcontractorCostValue);
  totalProfitPercentage = toPercent(totalProfitValue, totalCostValue);

  // Raw numeric percentages matching toPercent semantics (nonzero/0 -> 100)
  const percentValue = (profit, cost) =>
    cost !== 0 ? (profit / cost) * 100 : (profit !== 0 ? 100 : 0);
  const materialProfitPercentageValue = roundValue(percentValue(materialProfitValue, materialCostValue), 2);
  const laborProfitPercentageValue = roundValue(percentValue(laborProfitValue, laborCostValue), 2);
  const subcontractorProfitPercentageValue = roundValue(percentValue(subcontractorProfitValue, subcontractorCostValue), 2);
  const totalProfitPercentageValue = roundValue(percentValue(totalProfitValue, totalCostValue), 2);
  
  completionPercentage = `${roundValue(
    idx(totalCost, (_) => _.percentageComplete) || 0, 0
  )}%`; //|| toPercent(idx(sectionCost, _ => _.completionPercentage), 100)
  variance = (idx(totalCost, (_) => _.variance) || 0.0) + "%";
  utilisationRate = (idx(totalCost, (_) => _.utilisationRate) || 0.0) + "%";
  
  const rawEffortCompletion = idx(totalCost, (_) => _.effortCompletionPercentageValue) !== undefined 
    ? idx(totalCost, (_) => _.effortCompletionPercentageValue)
    : idx(totalCost, (_) => _.effortCompletionPercentage);
    
  effortCompletionPercentage = (parseFloat(rawEffortCompletion) || 0).toFixed(2) + "%";
  forecastHoursDisplay = (roundValue(idx(totalCost, (_) => _.forecastHours) || 0, 2)) + " hrs";

  return {
    materialSaleAmount,
    laborSaleAmount,
    subcontractorSaleAmount,
    totalSaleAmount,
    materialCostAmount,
    laborCostAmount,
    subcontractorCostAmount,
    totalCostAmount,
    materialProfit,
    laborProfit,
    subcontractorProfit,
    totalProfit,
    materialProfitPercentage,
    laborProfitPercentage,
    subcontractorProfitPercentage,
    totalProfitPercentage,
    completionPercentage,
    variance,
    utilisationRate,
    effortCompletionPercentage,
    forecastHoursDisplay,
    actualHoursDisplay: (roundValue(idx(totalCost, (_) => _.actualHours) || 0, 2)) + " hrs",
    varianceValue: roundValue(idx(totalCost, (_) => _.variance) || 0, 0),
    utilisationRateValue: roundValue(idx(totalCost, (_) => _.utilisationRate) || 0, 0),
    effortCompletionPercentageValue: roundValue(parseFloat(rawEffortCompletion) || 0, 2),
    materialProfitValue,
    laborProfitValue,
    subcontractorProfitValue,
    totalProfitValue,
    materialProfitPercentageValue,
    laborProfitPercentageValue,
    subcontractorProfitPercentageValue,
    totalProfitPercentageValue,
    forecastHoursValue: roundValue(idx(totalCost, (_) => _.forecastHours) || 0, 2),
    actualHoursValue: roundValue(idx(totalCost, (_) => _.actualHours) || 0, 2),
  };
};

export const findRootSection = ({
  stages = [],
  childSection = "",
  parentStage = null,
}) => {
  if (!childSection) return null;

  for (const stage of stages) {
    if (stage.stages) {
      const foundParent = findRootSection({
        stages: stage.stages,
        childSection,
        parentStage:
          parentStage || (stage.parent_stage_id === null ? stage : parentStage),
      });
      if (foundParent) return foundParent;
    } else if (stage.id === childSection)
      return parentStage || stages.find((s) => s.id === stage.parent_stage_id);
  }
  return null;
};
